import { defineStore } from 'pinia'
import { ref, computed, watch, nextTick } from 'vue'

import type { WorkspaceCatalogResponse, WorkspaceSearchItem } from '@/services/apiService'
import { resolveApiResourceUrl } from '@/services/apiUrlService'
import { connectivityService } from '@/services/connectivityService'
import { parseLineString, calculateBounds } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { loadWorkspaceCatalog } from '@/services/offlineContentProvider'
import { settingsService } from '@/services/settingsService'
import { TrackColorService } from '@/services/trackColorService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { useUiStore } from '@/stores/uiStore'
import {
  applyWorkspaceOverviewFilters,
  buildWorkspaceOverviewResults,
} from '@/utils/workspaceOverviewFilters'

export interface SelectedSearchWorkspace {
  clientId: string
  name: string
  color: string
  isVisible: boolean
  pointsLineReduced500m: [number, number][][] // Array of parsed coordinate arrays from LINESTRING geometries
}

interface AutoZoomRequest {
  bounds: ReturnType<typeof calculateBounds> | null
  force: boolean
}

export const useSearchWorkspaceStore = defineStore('searchWorkspace', () => {
  const uiStore = useUiStore()
  const selectedWorkspaces = ref<SelectedSearchWorkspace[]>([])
  const allWorkspaces = ref<WorkspaceSearchItem[]>([]) // Store all available workspaces
  const isVisibilityTemporarilySuppressed = ref(false)
  // Optimistic cover photo overrides: workspaceClientId → thumbnailUrl.
  // Persisted across bulk reloads so the UI stays consistent while a PATCH is in-flight.
  const pendingCoverUpdates = new Map<string, string | null>()
  const pendingPinnedUpdates = new Map<string, boolean>()
  const pendingAutoZoomRequest = ref<AutoZoomRequest | null>(null)
  const isInitialized = ref(false)
  const filteredWorkspaceIds = ref<Set<string> | null>(null) // null = no filter active (show all)
  const hoveredWorkspaceId = ref<string | null>(null)
  const overviewCounts = ref<{ displayed: number; total: number } | null>(null)
  const personalTourCount = ref(0)
  let inFlightWorkspacesPromise: Promise<WorkspaceSearchItem[]> | null = null
  let catalogGeneration = 0
  let lastLoadedAt = 0
  let lastCatalogSource: 'online' | 'offline' | null = null
  const isTauriApp = Boolean(import.meta.env.TAURI_ENV_TARGET_TRIPLE)

  const getCatalogSource = () =>
    connectivityService.isEffectivelyOnline.value ? ('online' as const) : ('offline' as const)

  // Save only the selected workspace IDs to localStorage
  const saveSelectedIds = () => {
    const selectedIds = selectedWorkspaces.value.map((c) => c.clientId)
    settingsService.saveSetting(UI_SETTING_KEYS.workspaceSearchSelectedWorkspaceIds, selectedIds)
  }

  // Save workspace visibility states to localStorage
  const saveVisibilityStates = () => {
    const visibilityStates: Record<string, boolean> = {}
    selectedWorkspaces.value.forEach((workspace) => {
      visibilityStates[workspace.clientId] = workspace.isVisible
    })
    settingsService.saveSetting(UI_SETTING_KEYS.workspaceSearchVisibilityStates, visibilityStates)
  }

  // Load workspace visibility states from localStorage
  const loadVisibilityStates = (): Record<string, boolean> => {
    return (
      settingsService.getSetting<Record<string, boolean>>(
        UI_SETTING_KEYS.workspaceSearchVisibilityStates,
      ) || {}
    )
  }

  // Load all workspaces once with in-flight de-duplication and basic caching
  const ensureWorkspacesLoaded = async (force = false): Promise<WorkspaceSearchItem[]> => {
    const catalogSource = getCatalogSource()

    if (!force && allWorkspaces.value.length > 0 && lastCatalogSource === catalogSource) {
      return allWorkspaces.value
    }
    // Skip force reload if data was loaded very recently (prevents duplicate
    // calls when init() and loadWorkspaces() run back-to-back on page load)
    if (
      force &&
      allWorkspaces.value.length > 0 &&
      lastCatalogSource === catalogSource &&
      Date.now() - lastLoadedAt < 2000
    ) {
      return allWorkspaces.value
    }
    if (inFlightWorkspacesPromise) {
      return inFlightWorkspacesPromise
    }
    const requestGeneration = catalogGeneration
    const request = loadWorkspaceCatalog()
      .then((catalog: WorkspaceCatalogResponse) => {
        if (requestGeneration !== catalogGeneration) return allWorkspaces.value
        setWorkspaceCatalog(catalog)
        lastLoadedAt = Date.now()
        lastCatalogSource = catalogSource
        return catalog.workspaces
      })
      .catch((error) => {
        if (requestGeneration !== catalogGeneration) return allWorkspaces.value
        // Handle authentication errors gracefully without logging them as errors
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } }
          if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
            logService.info(LOG_CATEGORIES.API, 'Authentication required for search workspaces', {
              status: axiosError.response.status,
            })
            setWorkspaceCatalog({ workspaces: [], personalTourCount: 0 }) // Clear workspaces when not authenticated
            return []
          }
        }

        // Log other errors and return empty array to prevent app crashes
        logService.error(LOG_CATEGORIES.API, 'Failed to load search workspaces', { error })
        setWorkspaceCatalog({ workspaces: [], personalTourCount: 0 })
        lastLoadedAt = Date.now()
        lastCatalogSource = catalogSource
        return []
      })
    inFlightWorkspacesPromise = request
    void request.finally(() => {
      if (inFlightWorkspacesPromise === request) {
        inFlightWorkspacesPromise = null
      }
    })
    return request
  }

  // Initialize store by loading selected IDs and restoring selections using cached workspaces
  const init = async () => {
    if (isInitialized.value) return
    isInitialized.value = true
    try {
      const savedIds = settingsService.getSetting<string[]>(
        UI_SETTING_KEYS.workspaceSearchSelectedWorkspaceIds,
      )
      if (savedIds && Array.isArray(savedIds) && savedIds.length > 0) {
        const workspaces = await ensureWorkspacesLoaded(false)

        // Restore selected workspaces based on saved IDs
        savedIds.forEach((id) => {
          const workspace = workspaces.find((c: WorkspaceSearchItem) => c.clientId === id)
          if (workspace) {
            addWorkspaceFromItem(workspace, { saveToStorage: false, autoZoom: false })
          }
        })
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to initialize search workspace store', {
        error,
      })
    }
  }

  const selectedWorkspaceIds = computed(() => selectedWorkspaces.value.map((c) => c.clientId))
  const overviewFilteredWorkspaceIds = computed(() => {
    if (allWorkspaces.value.length === 0) return null

    const results = buildWorkspaceOverviewResults(allWorkspaces.value, {
      query: uiStore.workspaceSearchQuery,
      sortBy: uiStore.workspaceSearchSortBy,
      sortDirection: uiStore.workspaceSearchSortDirection,
    })
    const filteredResults = applyWorkspaceOverviewFilters(results, {
      sharingFilter: uiStore.workspaceOverviewSharingFilter,
      offlineFilter: uiStore.workspaceOverviewOfflineFilter,
      isTauriApp,
    })

    return new Set(filteredResults.map((result) => result.clientId))
  })
  const visibleWorkspaces = computed(() =>
    selectedWorkspaces.value.filter(
      (c) =>
        !isVisibilityTemporarilySuppressed.value &&
        c.isVisible &&
        (filteredWorkspaceIds.value === null || filteredWorkspaceIds.value.has(c.clientId)),
    ),
  )
  const hasSelectedWorkspaces = computed(() => selectedWorkspaces.value.length > 0)

  watch(
    overviewFilteredWorkspaceIds,
    (ids) => {
      filteredWorkspaceIds.value = ids ? new Set(ids) : null
    },
    { immediate: true },
  )

  const queueAutoZoom = (
    bounds: ReturnType<typeof calculateBounds> | null = null,
    force = false,
  ) => {
    pendingAutoZoomRequest.value = {
      bounds,
      force,
    }
  }

  // Watch for changes in visible workspaces and auto-zoom when requested
  watch(
    () => ({
      workspaces: visibleWorkspaces.value,
      autoZoomRequest: pendingAutoZoomRequest.value,
    }),
    ({ workspaces, autoZoomRequest }) => {
      if (autoZoomRequest && workspaces.length > 0) {
        // Wait for Vue to update reactive state before calculating bounds
        nextTick(() => {
          const bounds = autoZoomRequest.bounds || getVisibleWorkspacesBounds()
          if (bounds) {
            window.dispatchEvent(
              new CustomEvent('zoom-to-search-workspaces', {
                detail: autoZoomRequest.force ? { bounds, force: true } : bounds,
              }),
            )
          }
          // Reset the auto-zoom request after handling
          pendingAutoZoomRequest.value = null
        })
      }
    },
    { flush: 'post' }, // Ensure this runs after all other watchers
  )

  const reconcileSelectedWorkspaces = (workspaces: WorkspaceSearchItem[]) => {
    const workspaceById = new Map(workspaces.map((workspace) => [workspace.clientId, workspace]))
    selectedWorkspaces.value = selectedWorkspaces.value.flatMap((selectedWorkspace) => {
      const refreshed = workspaceById.get(selectedWorkspace.clientId)
      if (!refreshed) {
        return []
      }

      const nextSelectedWorkspace = createSelectedWorkspaceFromItem(refreshed)
      nextSelectedWorkspace.isVisible = selectedWorkspace.isVisible
      nextSelectedWorkspace.color = selectedWorkspace.color
      return [nextSelectedWorkspace]
    })
  }

  watch(
    connectivityService.isEffectivelyOnline,
    async (online, wasOnline) => {
      if (!isInitialized.value || online === wasOnline) {
        return
      }

      const workspaces = await ensureWorkspacesLoaded(true)
      setWorkspaceCatalog({ workspaces, personalTourCount: personalTourCount.value })
      reconcileSelectedWorkspaces(workspaces)
      pruneUnavailableSelectedWorkspaces()
    },
    { flush: 'sync' },
  )

  // Generate a color for a workspace based on its ID using TrackColorService
  const generateWorkspaceColor = (clientId: string): string => {
    const availableColors = TrackColorService.getColors()

    // Use a simple hash of the ID to pick a color from available colors
    let hash = 0
    for (let i = 0; i < clientId.length; i++) {
      hash = ((hash << 5) - hash + clientId.charCodeAt(i)) & 0xffffffff
    }

    const colorIndex = Math.abs(hash) % availableColors.length
    return availableColors[colorIndex].value
  }

  // Helper function to create a SelectedSearchWorkspace from a WorkspaceSearchItem
  const createSelectedWorkspaceFromItem = (
    workspace: WorkspaceSearchItem,
  ): SelectedSearchWorkspace => {
    // Extract and parse all line_reduced_500m from all tracks in all tours
    const pointsLineReduced500m: [number, number][][] = []

    workspace.tours.forEach((tour) => {
      tour.tracks.forEach((track) => {
        if (track.pointsLineReduced500m) {
          const coordinates = parseLineString(track.pointsLineReduced500m)
          if (coordinates.length > 0) {
            pointsLineReduced500m.push(coordinates)
          }
        }
      })
    })

    // Check for saved visibility state, default to true if not found
    const savedVisibilityStates = loadVisibilityStates()
    const isVisible = savedVisibilityStates[workspace.clientId] ?? true

    return {
      clientId: workspace.clientId,
      name: workspace.name,
      color: generateWorkspaceColor(workspace.clientId),
      isVisible,
      pointsLineReduced500m,
    }
  }

  // Internal function to add workspace with optional localStorage save
  const addWorkspaceFromItem = (
    workspace: WorkspaceSearchItem,
    options: { saveToStorage?: boolean; autoZoom?: boolean; force?: boolean } = {},
  ) => {
    const { saveToStorage = true, autoZoom = true, force = true } = options

    if (selectedWorkspaceIds.value.includes(workspace.clientId)) {
      return
    }

    const newWorkspace = createSelectedWorkspaceFromItem(workspace)
    selectedWorkspaces.value.push(newWorkspace)

    if (saveToStorage) {
      saveSelectedIds()
    }

    if (autoZoom) {
      // Trigger auto-zoom to the newly added workspace when the user explicitly adds it.
      queueAutoZoom(calculateBounds(newWorkspace.pointsLineReduced500m), force)
    }
  }

  // Actions
  const addWorkspace = (workspace: WorkspaceSearchItem) => {
    addWorkspaceFromItem(workspace, { saveToStorage: true, autoZoom: true, force: true })
  }

  const removeWorkspace = (clientId: string) => {
    selectedWorkspaces.value = selectedWorkspaces.value.filter((c) => c.clientId !== clientId)
    saveSelectedIds()
  }

  const toggleWorkspaceVisibility = (clientId: string) => {
    const workspace = selectedWorkspaces.value.find((c) => c.clientId === clientId)
    if (workspace) {
      workspace.isVisible = !workspace.isVisible
      // Save visibility states to localStorage
      saveVisibilityStates()
    }
  }

  const setWorkspaceVisibility = (clientId: string, visible: boolean) => {
    const selectedWorkspace = selectedWorkspaces.value.find((c) => c.clientId === clientId)
    if (selectedWorkspace) {
      selectedWorkspace.isVisible = visible
      saveVisibilityStates()
      return
    }

    const catalogWorkspace = allWorkspaces.value.find(
      (workspace) => workspace.clientId === clientId,
    )
    if (!catalogWorkspace) return

    addWorkspaceFromItem(catalogWorkspace, {
      saveToStorage: true,
      autoZoom: false,
      force: false,
    })
    const createdWorkspace = selectedWorkspaces.value.find((c) => c.clientId === clientId)
    if (createdWorkspace) {
      createdWorkspace.isVisible = visible
      saveVisibilityStates()
    }
  }

  const clearAllWorkspaces = () => {
    isVisibilityTemporarilySuppressed.value = false
    selectedWorkspaces.value = []
    saveSelectedIds()
  }

  const isWorkspaceSelected = (clientId: string): boolean => {
    return selectedWorkspaceIds.value.includes(clientId)
  }

  const getWorkspaceById = (clientId: string): SelectedSearchWorkspace | undefined => {
    return selectedWorkspaces.value.find((c) => c.clientId === clientId)
  }

  // Calculate combined bounding box of all visible workspaces
  const getVisibleWorkspacesBounds = () => {
    const visibleWorkspacesList = visibleWorkspaces.value
    if (visibleWorkspacesList.length === 0) return null

    // Collect all coordinate arrays from all visible workspaces
    const allCoordinateArrays = visibleWorkspacesList.flatMap(
      (workspace) => workspace.pointsLineReduced500m,
    )

    return calculateBounds(allCoordinateArrays)
  }

  // Set all available workspaces (called when workspaces are loaded).
  // Always resolves cover photo thumbnail URLs so non-localhost devices work correctly.
  // Re-applies any pending optimistic cover overrides so a bulk server reload doesn't
  // clobber a cover change that is still syncing.
  const setAllWorkspaces = (workspaces: WorkspaceSearchItem[]) => {
    // If the caller passes back the exact same array reference that's already in the store
    // (e.g. ensureWorkspacesLoaded returned allWorkspaces.value from its cache path),
    // re-processing it would incorrectly clear pendingCoverUpdates entries because the
    // pending URL has already been stamped onto those items in the previous pass.
    if (workspaces === allWorkspaces.value) return
    allWorkspaces.value = workspaces.map((w) => {
      const resolved = w.coverPhotoThumbnailUrl
        ? { ...w, coverPhotoThumbnailUrl: resolveApiResourceUrl(w.coverPhotoThumbnailUrl) }
        : w
      if (pendingCoverUpdates.has(w.clientId)) {
        // If the server data already agrees with our optimistic value, clear the pending entry.
        const pending = pendingCoverUpdates.get(w.clientId)
        if (resolved.coverPhotoThumbnailUrl === pending) {
          pendingCoverUpdates.delete(w.clientId)
        } else {
          return { ...resolved, coverPhotoThumbnailUrl: pending ?? null }
        }
      }
      if (pendingPinnedUpdates.has(w.clientId)) {
        const pending = pendingPinnedUpdates.get(w.clientId)
        if (resolved.isPinned === pending) {
          pendingPinnedUpdates.delete(w.clientId)
        } else {
          return {
            ...resolved,
            isPinned: pending,
            tours: resolved.tours.map((tour) => ({ ...tour, isPinned: pending })),
          }
        }
      }
      return resolved
    })
  }

  const setPersonalTourCount = (count: number) => {
    personalTourCount.value = count
  }

  const setWorkspaceCatalog = (catalog: WorkspaceCatalogResponse) => {
    const resolvedWorkspaces = catalog.workspaces.map((w) =>
      w.coverPhotoThumbnailUrl
        ? { ...w, coverPhotoThumbnailUrl: resolveApiResourceUrl(w.coverPhotoThumbnailUrl) }
        : w,
    )
    setAllWorkspaces(resolvedWorkspaces)
    setPersonalTourCount(catalog.personalTourCount)
  }

  const pruneUnavailableSelectedWorkspaces = () => {
    const availableIds = new Set(allWorkspaces.value.map((workspace) => workspace.clientId))
    const nextSelected = selectedWorkspaces.value.filter((workspace) =>
      availableIds.has(workspace.clientId),
    )

    if (nextSelected.length !== selectedWorkspaces.value.length) {
      selectedWorkspaces.value = nextSelected
      saveSelectedIds()
      saveVisibilityStates()
    }
  }

  const activateAllWorkspaces = () => {
    isVisibilityTemporarilySuppressed.value = false
    const savedVisibilityStates = loadVisibilityStates()
    let changesMade = false

    allWorkspaces.value.forEach((workspaceItem) => {
      const existingWorkspace = selectedWorkspaces.value.find(
        (c) => c.clientId === workspaceItem.clientId,
      )

      if (existingWorkspace) {
        // If workspace already exists, only restore its saved visibility state if it was explicitly saved
        // Don't override current visibility state unless we have a saved preference
        const hasSavedVisibility = savedVisibilityStates.hasOwnProperty(workspaceItem.clientId)
        if (hasSavedVisibility) {
          const savedVisibility = savedVisibilityStates[workspaceItem.clientId]
          if (existingWorkspace.isVisible !== savedVisibility) {
            existingWorkspace.isVisible = savedVisibility
            changesMade = true
          }
        }
        // If no saved visibility state, keep current state (don't force to visible)
      } else {
        // Workspace not in selectedWorkspaces, create and add it.
        const pointsLineReduced500m: [number, number][][] = []
        workspaceItem.tours.forEach((tour) => {
          tour.tracks.forEach((track) => {
            if (track.pointsLineReduced500m) {
              const coordinates = parseLineString(track.pointsLineReduced500m)
              if (coordinates.length > 0) {
                pointsLineReduced500m.push(coordinates)
              }
            }
          })
        })

        // For new workspaces, use saved visibility state, or default to visible if no saved state
        const savedVisibility = savedVisibilityStates[workspaceItem.clientId] ?? true

        const newSelectedWorkspace: SelectedSearchWorkspace = {
          clientId: workspaceItem.clientId,
          name: workspaceItem.name,
          color: generateWorkspaceColor(workspaceItem.clientId),
          isVisible: savedVisibility,
          pointsLineReduced500m,
        }
        selectedWorkspaces.value.push(newSelectedWorkspace)
        changesMade = true
      }
    })

    if (changesMade) {
      saveSelectedIds()
      saveVisibilityStates()
      // After all workspaces are processed and potentially made visible,
      // zoom to the bounds of all currently visible workspaces.
      queueAutoZoom()
    }
  }

  // Hide all workspaces but preserve selection state (for navigation - temporary)
  const hideAllWorkspacesTemporary = () => {
    isVisibilityTemporarilySuppressed.value = true
  }

  // Hide all workspaces and save state (for user action)
  const hideAllWorkspaces = () => {
    isVisibilityTemporarilySuppressed.value = false
    selectedWorkspaces.value.forEach((workspace) => {
      workspace.isVisible = false
    })
    // Save visibility states when used as user action
    saveVisibilityStates()
  }

  // Save current visibility states before hiding for navigation
  const saveCurrentVisibilityForNavigation = () => {
    if (selectedWorkspaces.value.length === 0) {
      return
    }
    saveVisibilityStates()
  }

  // Hide workspaces for navigation (saves current state first)
  const hideAllWorkspacesForNavigation = () => {
    saveCurrentVisibilityForNavigation()
    hideAllWorkspacesTemporary()
  }

  // Show all selected workspaces
  const showSelectedWorkspaces = () => {
    isVisibilityTemporarilySuppressed.value = false
    selectedWorkspaces.value.forEach((workspace) => {
      workspace.isVisible = true
    })
    saveVisibilityStates()
  }

  // Restore workspaces with their saved visibility states (preserves individual toggle states)
  const restoreWorkspaceVisibility = () => {
    isVisibilityTemporarilySuppressed.value = false
    const savedVisibilityStates = loadVisibilityStates()
    let changesMade = false

    selectedWorkspaces.value.forEach((workspace) => {
      // If we have a saved visibility state, use it; otherwise default to visible
      const savedVisibility = savedVisibilityStates.hasOwnProperty(workspace.clientId)
        ? savedVisibilityStates[workspace.clientId]
        : true

      if (workspace.isVisible !== savedVisibility) {
        workspace.isVisible = savedVisibility
        changesMade = true
      }
    })

    if (changesMade) {
      // Don't save again since we're just restoring
    }
  }

  const clearTemporaryVisibilitySuppression = () => {
    isVisibilityTemporarilySuppressed.value = false
  }

  const setHoveredWorkspaceId = (id: string | null) => {
    hoveredWorkspaceId.value = id
  }

  const setOverviewCounts = (displayed: number, total: number) => {
    overviewCounts.value = { displayed, total }
  }

  /**
   * Update the cover photo thumbnail URL for a single workspace in the catalog.
   * Also records the value as a pending optimistic override so that subsequent
   * bulk reloads (from handleBackToWorkspaces) don't clobber it while the PATCH
   * is still in-flight.
   */
  const updateWorkspaceCoverPhoto = (clientId: string, coverPhotoThumbnailUrl: string | null) => {
    pendingCoverUpdates.set(clientId, coverPhotoThumbnailUrl)
    const workspace = allWorkspaces.value.find((w) => w.clientId === clientId)
    if (workspace) {
      workspace.coverPhotoThumbnailUrl = coverPhotoThumbnailUrl
    }
  }

  const updateWorkspaceBookmark = (clientId: string, isBookmarked: boolean) => {
    const workspace = allWorkspaces.value.find((w) => w.clientId === clientId)
    if (workspace) {
      workspace.isBookmarked = isBookmarked
    }
  }

  const updateWorkspacePinned = (clientId: string, isPinned: boolean) => {
    pendingPinnedUpdates.set(clientId, isPinned)
    const workspace = allWorkspaces.value.find((w) => w.clientId === clientId)
    if (workspace) {
      workspace.isPinned = isPinned
      workspace.tours.forEach((tour) => {
        tour.isPinned = isPinned
      })
    }
  }

  const removeWorkspaceFromCatalog = (clientId: string) => {
    allWorkspaces.value = allWorkspaces.value.filter((workspace) => workspace.clientId !== clientId)
    pruneUnavailableSelectedWorkspaces()
  }

  const resetPrivateWorkspaceCatalog = () => {
    catalogGeneration += 1
    selectedWorkspaces.value = []
    allWorkspaces.value = []
    personalTourCount.value = 0
    hoveredWorkspaceId.value = null
    filteredWorkspaceIds.value = null
    overviewCounts.value = null
    pendingAutoZoomRequest.value = null
    isInitialized.value = false
    isVisibilityTemporarilySuppressed.value = false
    pendingCoverUpdates.clear()
    pendingPinnedUpdates.clear()
    inFlightWorkspacesPromise = null
    lastLoadedAt = 0
    lastCatalogSource = null
    saveSelectedIds()
    saveVisibilityStates()
  }

  return {
    // State
    selectedWorkspaces,
    allWorkspaces,
    isInitialized,
    isVisibilityTemporarilySuppressed,
    hoveredWorkspaceId,
    personalTourCount,

    // Computed
    selectedWorkspaceIds,
    visibleWorkspaces,
    hasSelectedWorkspaces,

    // Actions
    init,
    ensureWorkspacesLoaded,
    addWorkspace,
    removeWorkspace,
    toggleWorkspaceVisibility,
    setWorkspaceVisibility,
    clearAllWorkspaces,
    isWorkspaceSelected,
    getWorkspaceById,
    getVisibleWorkspacesBounds,
    setAllWorkspaces,
    setPersonalTourCount,
    setWorkspaceCatalog,
    reconcileSelectedWorkspaces,
    pruneUnavailableSelectedWorkspaces,
    activateAllWorkspaces,
    hideAllWorkspaces,
    hideAllWorkspacesTemporary,
    hideAllWorkspacesForNavigation,
    showSelectedWorkspaces,
    restoreWorkspaceVisibility,
    clearTemporaryVisibilitySuppression,
    setHoveredWorkspaceId,
    overviewCounts,
    setOverviewCounts,
    updateWorkspaceCoverPhoto,
    updateWorkspaceBookmark,
    updateWorkspacePinned,
    removeWorkspaceFromCatalog,
    resetPrivateWorkspaceCatalog,
    generateWorkspaceColor,
    clearPendingCoverUpdate: (clientId: string) => pendingCoverUpdates.delete(clientId),
  }
})
